import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLazyQuery } from '@apollo/client'
import { useNavigate, useParams } from 'react-router-dom'
import { Typography, Popover } from '@mui/material'
import { Box, Stack } from '@mui/system'
import useLink from '../hooks/use-link'
import { useUser } from 'store'

import Status from 'components/global/Status'
import { GET_CENTER_DOCUMENT_DOWNLOAD } from '../operations-gql/queries'
import CardActionButton from 'components/global/CardActions/CardActionButton'
import CardActionsButtonsGroup from 'components/global/CardActions/CardActionsButtonsGroup'

export const StatusWrapper = ({ step, uppercase, isCenterDocument }) => {
  const { t } = useTranslation()

  const statusProps = (step === 'validated' || isCenterDocument)
    ? {
      status: t('audin_document.status.validated_one'),
      color: '#587F31',
      bgcolor: '#E6F0D7',
      height: 24,
      fontSize: 10,
      isUppercase: uppercase,
      borderRadius: '50px'
    }
    : {
      status: t('audin_document.status.in_progress'),
      color: '#5D5D5D',
      bgcolor: '#E7E7E7',
      height: 24,
      fontSize: 10,
      isUppercase: uppercase,
      borderRadius: '50px'
    }

  return <Status {...statusProps} />
}

export const Actions = ({ item }) => {
  const apiLink = useLink()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { type: documentType } = useParams()
  const [user] = useUser()

  const isCenterDocument = documentType === 'center'

  const [getDownloadUrl] = useLazyQuery(GET_CENTER_DOCUMENT_DOWNLOAD)

  const handleEdit = (e) => {
    e.preventDefault()
    e.stopPropagation()
    navigate(`/documents/${documentType}/${item.id}/edit`)
  }

  const handleDownload = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    const { data } = await getDownloadUrl({ variables: { id: item.id } })
    if (data && data.getCenterDocumentDownload) {
      const link = document.createElement('a')
      link.href = data.getCenterDocumentDownload.url
      link.setAttribute('download', item.name)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const handleView = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    const { data } = await getDownloadUrl({ variables: { id: item.id } })
    if (data && data.getCenterDocumentDownload) {
      window.open(data.getCenterDocumentDownload.url, '_blank')
    }
  }

  const handleDelete = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    navigate(`/documents/${documentType}/${item.id}/delete`)
  }

  const handleAudinDocumentView = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    await apiLink.go(item.id)
  }

  const hasWifirstDocumentsRole = user.roles && user.roles.wifirst_documents

  return (
    <CardActionsButtonsGroup>
      {isCenterDocument && (
        <>
          {item.content_type === 'application/pdf' && (
            <CardActionButton
              tooltiptitle={t('audin_document.table.actions.view')}
              iconlabel="eye"
              onClick={handleView}
            />
          )}
          <CardActionButton
            tooltiptitle={t('audin_document.table.actions.download')}
            iconweight="solid"
            iconlabel="arrow-down"
            onClick={handleDownload}
          />
          
          {hasWifirstDocumentsRole && (
            <>
              <CardActionButton
                tooltiptitle={t('audin_document.table.actions.edit')}
                iconlabel="pen"
                onClick={handleEdit}
              />
              <CardActionButton
                tooltiptitle={t('audin_document.table.actions.delete')}
                iconlabel="trash"
                onClick={handleDelete}
              />
            </>
          )}
        </>
      )}
      {!isCenterDocument && item.step === 'validated' && (
        <CardActionButton
          tooltiptitle={t('audin_document.table.actions.consult')}
          iconlabel="arrow-up-right"
          onClick={handleAudinDocumentView}
        />
      )}
    </CardActionsButtonsGroup>
  )
}

export const AccountSiteName = ({ item, isAccount }) => {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState(null)

  const handlePopoverOpen = (event) => {
    if ((isAccount && item.count_accounts > 1) || (!isAccount && item.count_sites > 1)) {
      setAnchorEl(event.currentTarget)
    }
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  const getContent = () => {
    if (item.accounts_summary) {
      if (isAccount) {
        return item.count_accounts === 0 ? '-' : 
               item.count_accounts === 1 ? item.accounts_summary[0].name :
               item.count_accounts.toString()
      } else {
        const siteCount = item.count_sites
        return siteCount === 0 ? '-' :
               siteCount === 1 ? item.accounts_summary[0].sites[0].name :
               siteCount.toString()
      }
    } else {
      return isAccount ? item.organism : item.site.name
    }
  }

  const content = getContent()

  const getPopoverContent = () => {
    if (item.accounts_summary) {
      if (isAccount) {
        return item.count_accounts > 1 ? (
          <Typography fontSize={11} fontWeight={500}>
            {t('audin_document.table.tooltips.click_for_details')}
          </Typography>
        ) : (
          item.accounts_summary.map(account => (
            <Typography key={account.name} fontSize={11} fontWeight={500}>
              <li>{account.name}</li>
            </Typography>
          ))
        )
      } else {
        const allSites = item.accounts_summary.flatMap(account => account.sites)
        return allSites.length > 10 ? (
          <Typography fontSize={11} fontWeight={500}>
            {t('audin_document.table.tooltips.click_for_details')}
          </Typography>
        ) : (
          allSites.map(site => (
            <Typography key={site.name} fontSize={11} fontWeight={500}>
              <li>{site.name}</li>
            </Typography>
          ))
        )
      }
    }
  }

  const shouldShowCursor = isAccount 
    ? item.count_accounts > 10 
    : item.accounts_summary?.flatMap(account => account.sites).length > 10

  return (
    <Box>
      <Typography 
        variant="caption" 
        onMouseEnter={handlePopoverOpen} 
        onMouseLeave={handlePopoverClose}
        style={{ cursor: shouldShowCursor ? 'pointer' : 'default' }}
      >
        {content}
      </Typography>
      <Popover
        sx={{ pointerEvents: 'none' }}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Stack color={'white'} bgcolor={'grey.700'} p={'4px 6px'}>
          {getPopoverContent()}
        </Stack>
      </Popover>
    </Box>
  )
}